/**
 * API client for the cross-entity global search / command palette
 * (issue #1440, federated universal search EPIC #1512 / #1513 / #1515).
 *
 * Calls the read-only ``GET /api/v1/search/?q=`` aggregator. Results are SCOPED
 * server-side to the caller's permissions (an agent sees only their own
 * records); the frontend never widens visibility.
 *
 * The schema below is the federated superset delivered by #1513 (a strict
 * superset of the original #1440 shape): each group carries an ``icon`` (a
 * Lucide name string), a ``total_matches`` count (full count beyond the
 * per-entity cap) and a ``see_all_url`` deep link; each result carries a
 * relevance ``score`` in ``[0, 1]`` (HIGHER is better) and optional ``match``
 * highlight offsets on the label.
 */
import { apiRequest } from "./client";

export type SearchEntityType =
  | "contact"
  | "listing"
  | "buyer"
  | "deal"
  | "farm"
  | "task"
  | "conveyancer"
  | "template"
  | "document";

/** Highlight offsets on a result label (mirrors the registry CommandMatch). */
export interface SearchMatch {
  field: "label" | "sublabel";
  start: number;
  length: number;
}

export interface SearchResult {
  id: number | string;
  type: SearchEntityType;
  label: string;
  sublabel: string;
  /** CRM-relative navigation path, e.g. "/crm/contacts/42". */
  url: string;
  /** Cross-group relevance in [0, 1]; HIGHER is better (#1513). */
  score: number;
  /** Highlight offsets when the query is a literal substring of the label. */
  match: SearchMatch | null;
}

export interface SearchGroup {
  type: SearchEntityType;
  label: string;
  /** Lucide icon NAME (a string), resolved to a component on the frontend. */
  icon: string;
  /** Full match count beyond the per-entity cap (drives "See all N"). */
  total_matches: number;
  /** Module filtered-list deep link, e.g. "/crm/contacts?q=…". */
  see_all_url: string;
  results: SearchResult[];
}

export interface SearchResponse {
  query: string;
  min_query_length: number;
  groups: SearchGroup[];
  total: number;
}

/**
 * Run a global search. ``signal`` lets the caller abort a stale request when
 * the debounced query advances.
 */
export async function globalSearch(
  query: string,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  const qs = new URLSearchParams({ q: query }).toString();
  return apiRequest<SearchResponse>(`/search/?${qs}`, {}, signal);
}
